import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import { FormattedMessage } from 'react-intl';
import { AccordionButton, useAccordionItemContext } from '@reach/accordion';
import '@reach/accordion/styles.css';
import RouteBadge from '#/shared/components/RouteBadge';
import * as StopSign from '#/transit-tracker/components/StopSign';
import StopSignPanel from '#/transit-tracker/components/StopSignPanel';
import { ScreenReaderText } from '#/shared/components/Accessibility';
import StopSignListSkeletons from '#/shared/components/Skeletons/StopSignListSkeletons';
import ArrivalTimeBadge from '#/transit-tracker/components/ArrivalTimeBadge';
import Button from '#/shared/components/Buttons';
import FavoriteButton from '#/transit-tracker/components/FavoriteButton';
import { useGetStopAlertsQuery } from '#/services/developerApi';
import useNavigate from '#/shared/hooks/useNavigate';
import useSelectedLocation from '#/shared/hooks/useSelectedLocation';
import ROUTES from '#/constants/routes';
import { stopType } from '#/prop-types';
import * as Styled from './styled';

function SeeNearbyServiceButton(stop) {
  const {
    stop: { lat, lng },
    desc: locationDescription,
  } = stop;
  const { navigate } = useNavigate();
  const { setSelectedLocationAndRedirect } = useSelectedLocation();

  const onButtonClickNavigate = () =>
    setSelectedLocationAndRedirect({ lat, lng }, () =>
      navigate(ROUTES.location, locationDescription)
    );

  return (
    <Styled.ButtonWrap>
      <Button variant="outline" onClick={onButtonClickNavigate}>
        <FormattedMessage id="detailview.seenearbyservice" />
      </Button>
    </Styled.ButtonWrap>
  );
}

function AccordionStopNavigationButton(props) {
  const { isExpanded } = useAccordionItemContext();
  return (
    <StopSign.StopNavigationButton
      as={AccordionButton}
      // TODO: find a way to get the props array elements without spreading props
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
      $collapsed={!isExpanded}
    />
  );
}

function StopSignList({
  stop,
  signs,
  toggleFavoriteStop,
  isFavoriteStop,
  navigateToVehicleTracker,
  navigateToSchedule,
  navigateToRouteAlerts,
  navigateToStopAndSign,
  routeId,
}) {
  const [indices, setIndices] = React.useState([]);
  const stopRowOverrideStyles = `
    border: none;
    margin: 0;
    &:hover {
      box-shadow: none;
    }`;

  const stopID = Number(stop?.id);
  const { data: alertsData } = useGetStopAlertsQuery(stop?.id);

  const filteredAlertsData = alertsData?.stopAlerts?.alerts.filter(alert => {
    return (
      (!alert.location && alert?.route?.[0]?.no_service_flag) ||
      alert.location?.some(loc => loc.id === stopID)
    );
  });

  const stopHasAlerts = filteredAlertsData?.length !== 0;

  // TODO: Replace with `useLocation` hook from Reach Router when upgraded
  //  to latest version of Gatsby (which dictates the router version)
  const urlQuery = typeof window !== 'undefined' ? window.location.search : '';
  const skeletonMode = !stop;

  useEffect(() => {
    let nextIndices = [];
    const queryParams = queryString.parse(urlQuery);
    if (signs) {
      if (queryParams.sign) {
        // fetch stop signs from the querystring params and open arrivals
        // that correspond to them
        const querySigns = [].concat(queryParams.sign);
        nextIndices = Object.keys(signs).reduce((array, k, ix) => {
          if (querySigns.includes(k)) array.push(ix);
          return array;
        }, []);
      } else if (routeId) {
        // if the exact stop signs aren't know and a route id is provided
        // open all signs that belong to the supplied route
        nextIndices = Object.keys(signs).reduce((array, k, ix) => {
          if (signs[k].route.id.toString() === routeId) array.push(ix);
          return array;
        }, []);
      }
    }

    setIndices(nextIndices.sort());
  }, [routeId, signs, urlQuery]);

  function updateStopSignQueryParams(toggledIx) {
    let nextIndices = [];

    if (indices.includes(toggledIx)) {
      nextIndices = indices.filter(currentIx => currentIx !== toggledIx);
    } else {
      nextIndices = [...indices, toggledIx];
    }
    if (stop) {
      navigateToStopAndSign(
        stop.id,
        nextIndices.length
          ? Object.keys(signs).filter((s, ix) => nextIndices.includes(ix))
          : null
      );
    }
  }
  const JSX = () => {
    const alertsIconAndAltMessage = (
      <div>
        {filteredAlertsData?.map(alert => (
          <Styled.AlertWrapper key={alert.id}>
            <Styled.IconWrapper>
              <Styled.AlertsIcon />
            </Styled.IconWrapper>
            <Styled.AlertsMessageContainer messageLength={alert?.desc?.length}>
              <Styled.AlertsMessageTextAlignment>
                {alert.desc}
                {alert?.info_link_url && (
                  <Styled.AlertMoreLink href={alert.info_link_url}>
                    <FormattedMessage id="alerts.more" />
                  </Styled.AlertMoreLink>
                )}
              </Styled.AlertsMessageTextAlignment>
            </Styled.AlertsMessageContainer>
          </Styled.AlertWrapper>
        ))}
      </div>
    );
    let result;
    if (skeletonMode) {
      result = <StopSignListSkeletons />;
    } else if (signs && Object.keys(signs).length) {
      const signAccordionRows = Object.entries(signs).map(
        ([signName, signData], index) => {
          const { arrivals, route, stopName, vanityName } = signData;
          const firstArrival = arrivals[0];

          // `signRoute` is the route id shown on the vehicle head-sign,
          // `id` is the route id in the schedule data; we want to show
          // the user what they'll be seeing in the field where possible
          const routeBadgeId = route.signRoute || route.id;

          return (
            <Styled.SignRowAccordionItem key={signName}>
              <StopSign.StopRow css={stopRowOverrideStyles}>
                <AccordionStopNavigationButton>
                  <StopSign.RouteColumn>
                    <RouteBadge routeId={routeBadgeId} />
                  </StopSign.RouteColumn>
                  <StopSign.ArrivalDescriptionColumn>
                    <StopSign.VanityNameDiv>
                      <FormattedMessage
                        id="arrivals.toname"
                        values={{ name: vanityName }}
                      />
                    </StopSign.VanityNameDiv>
                    <StopSign.StopName>
                      <ScreenReaderText>
                        <FormattedMessage id="arrivals.details_acc" />
                      </ScreenReaderText>
                      {stopName}
                    </StopSign.StopName>
                  </StopSign.ArrivalDescriptionColumn>
                  <StopSign.ArrivalTimeColumn>
                    {!indices.includes(index) && (
                      <ArrivalTimeBadge arrival={firstArrival} />
                    )}
                  </StopSign.ArrivalTimeColumn>
                  <ScreenReaderText>
                    <FormattedMessage id="arrivals.details_acc" />
                  </ScreenReaderText>
                </AccordionStopNavigationButton>
                <FavoriteButton
                  isFavorite={isFavoriteStop(firstArrival)}
                  toggleFavorite={() => toggleFavoriteStop(firstArrival)}
                  type="stop"
                />
              </StopSign.StopRow>
              <Styled.AccordionPanel>
                <StopSignPanel
                  sign={signData}
                  navigateToVehicleTracker={navigateToVehicleTracker}
                  navigateToSchedule={navigateToSchedule}
                  navigateToRouteAlerts={navigateToRouteAlerts}
                />
              </Styled.AccordionPanel>
            </Styled.SignRowAccordionItem>
          );
        }
      );
      if (stopHasAlerts) {
        result = (
          <>
            {signAccordionRows}
            <Styled.WithStopsHelper>
              {alertsIconAndAltMessage}
            </Styled.WithStopsHelper>
          </>
        );
      } else {
        result = signAccordionRows;
      }
    } else if (stopHasAlerts) {
      result = (
        <>
          {alertsIconAndAltMessage}
          <SeeNearbyServiceButton stop={stop} />
        </>
      );
    } else {
      result = (
        <>
          <Styled.IconWrapper>
            <Styled.AlertsIcon />
          </Styled.IconWrapper>
          <Styled.NoArrivalMessage>
            <FormattedMessage id="nearbystops.noarrivals" />
          </Styled.NoArrivalMessage>
          <SeeNearbyServiceButton stop={stop} />
        </>
      );
    }

    return result;
  };

  return (
    <Styled.SignListAccordion
      index={indices}
      onChange={ix => updateStopSignQueryParams(ix)}
    >
      {JSX()}
    </Styled.SignListAccordion>
  );
}

StopSignList.propTypes = {
  /** Contains details about the stop */
  stop: stopType,
  /** A collection of associated signs containing arrivals and route data */
  signs: PropTypes.shape({}),
  /**
   * Takes the combination of a stop and line ID and adds/removes it from a
   * favorite stops collection
   */
  toggleFavoriteStop: PropTypes.func.isRequired,
  /** Checks if a given stop and line are favorited */
  isFavoriteStop: PropTypes.func.isRequired,
  /** Sends the user to a specific vehicle detail view */
  navigateToVehicleTracker: PropTypes.func.isRequired,
  /** Sends the user to a specific routes stop schedule page */
  navigateToSchedule: PropTypes.func.isRequired,
  /** Sends the user to a specific route alerts page */
  navigateToRouteAlerts: PropTypes.func.isRequired,
  /** Sends the user to a specific stop page with supplied signs opened */
  navigateToStopAndSign: PropTypes.func.isRequired,
  /** Optional param to programmatically expand a routes card */
  routeId: PropTypes.string,
};
StopSignList.defaultProps = {
  routeId: null,
  signs: {},
  stop: null,
};
export default StopSignList;
